import { useId, useState } from 'react'
import { Icon } from '../components/Icon'
import { Modal } from '../components/Modal'
import { useReference } from '../data/ReferenceContext'
import type { ServiceOffering } from '../domain/types'

export interface AddStepInput {
  serviceId: number
  serviceConfigId: number
  sessionId: number
  required?: boolean
  allowSkip?: boolean
  estimatedDurationMinutes?: number
}

export function AddStepModal({ aggregatorId, submitting = false, onClose, onConfirm }: {
  aggregatorId: string
  submitting?: boolean
  onClose: () => void
  onConfirm: (input: AddStepInput) => void
}) {
  const titleId = useId()
  const { services: SERVICES, offeringsForService } = useReference()
  const [selServiceId, setSelServiceId] = useState<number | null>(null)
  const [selOffering, setSelOffering] = useState<ServiceOffering | null>(null)

  const offerings = selServiceId != null ? offeringsForService(selServiceId) : []

  return (
    <Modal
      open
      onClose={submitting ? () => {} : onClose}
      titleId={titleId}
      title="Adicionar etapa"
      closeOnOverlay={!submitting}
    >
      <div data-screen-label="Adicionar etapa">
        <div className="fz-modal__icon"><Icon name="plus" size={24} /></div>
        <div className="fz-modal__title" id={titleId}>Adicionar etapa</div>
        <div className="fz-modal__text">
          Escolha o serviço, fila e sessão para a nova etapa da jornada <b>{aggregatorId.slice(0, 8)}…</b>.
        </div>

        {!selServiceId && (
          <div className="fz-field">
            <label className="fz-field__label">Serviço</label>
            <div className="fz-transfer__opts">
              {SERVICES.map((s) => (
                <button
                  key={s.serviceId}
                  className={'fz-transfer__opt ' + (selServiceId === s.serviceId ? 'is-active' : '')}
                  disabled={submitting}
                  onClick={() => { setSelServiceId(s.serviceId); setSelOffering(null) }}
                >
                  <span><span className="fz-svcchip__code">{s.code}</span> {s.nome}</span>
                  <span className="fz-transfer__sala">{s.estMin ? `~${s.estMin} min` : ''}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {selServiceId != null && !selOffering && (
          <div className="fz-field">
            <label className="fz-field__label">Fila / Sessão</label>
            <div className="fz-transfer__opts">
              {offerings.length === 0 && <div className="fz-sessioncal__empty">Nenhuma oferta disponível para este serviço.</div>}
              {offerings.map((o) => (
                <button
                  key={o.serviceConfigId + ':' + o.sessionId}
                  className={'fz-transfer__opt ' + (selOffering === o ? 'is-active' : '')}
                  disabled={submitting}
                  onClick={() => setSelOffering(o)}
                >
                  <span><span className="fz-svcchip__code">{o.code}</span> {o.serviceName}</span>
                  <span className="fz-transfer__sala">{o.filaNome} · {o.sala}{o.sessionLabel !== 'Dia inteiro' ? ' · ' + o.sessionLabel : ''}</span>
                </button>
              ))}
            </div>
            <button className="fz-btn fz-btn--ghost fz-btn--sm" onClick={() => { setSelServiceId(null); setSelOffering(null) }} disabled={submitting}>Escolher outro servico</button>
          </div>
        )}

        {selOffering && (
          <div className="fz-field">
            <label className="fz-field__label">Confirmar</label>
            <div className="fz-transfer__opts">
              {/* Info tile: antes era <button> interativo preso no focus trap sem ação (audit modais #8). */}
              <div className="fz-transfer__opt is-active">
                <span><span className="fz-svcchip__code">{selOffering.code}</span> {selOffering.serviceName}</span>
                <span className="fz-transfer__sala">{selOffering.filaNome} · {selOffering.sala}{selOffering.sessionLabel !== 'Dia inteiro' ? ' · ' + selOffering.sessionLabel : ''}</span>
              </div>
            </div>
          </div>
        )}

        <div className="fz-modal__actions">
          <button className="fz-btn fz-btn--neutral fz-btn--block" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button className="fz-btn fz-btn--primary fz-btn--block" disabled={!selOffering || submitting} aria-busy={submitting}
            onClick={() => selOffering && !submitting && onConfirm({
              serviceId: selOffering.serviceId,
              serviceConfigId: selOffering.serviceConfigId,
              sessionId: selOffering.sessionId,
            })}>
            {submitting ? <><span className="fz-spin" /> Adicionando…</> : 'Adicionar etapa'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
